import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {apiV2} from "api/firebase";
import {LOGIN_PATH} from "view/routes";
import useSearchParam from "hooks/useSearchParam";
import useGoogleInviteSignUp from "hooks/useGoogleInviteSignUp";
import TwoFactorChallenge from "view/components/TwoFactorChallenge";
import {loginUser} from "tools/auth/loginUser";
import SignUpPresentation from "./presentation";
import useQueryParams from "./helpers/useQueryParams";

// What `auth/invite/token` answers for a link that can no longer be accepted:
// bad, expired or used (401), the wrong role (403), the account gone (404).
// Anything else is a failed check, not a dead link — the form stays.
const DEAD_INVITE_STATUSES = [401, 403, 404];

const SignUp = () => {
  const token = useSearchParam("token") ?? "";
  const {email} = useQueryParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isLinkDead, setIsLinkDead] = useState(false);
  // Captured during render, before the Google hook's effect strips the
  // fragment — the password-exists redirect below must not fire mid-callback.
  const [isGoogleCallback] = useState(() =>
    window.location.hash.includes("provider=google")
  );

  const {
    isBusy,
    googleError,
    isInviteInvalid,
    twoFactor,
    startGoogleSignUp,
    completeSignIn,
    resetTwoFactor,
  } = useGoogleInviteSignUp({
    returnPath: "/sign-up",
  });

  useEffect(() => {
    const checkInvite = async () => {
      // An account that already has a password belongs on the login page,
      // however its invite link is doing.
      if (email && !isGoogleCallback) {
        try {
          const {
            exist: isUserExists,
          } = await apiV2.auth.checkIfUserPasswordExists({
            email,
          });

          if (isUserExists) {
            navigate(LOGIN_PATH, {replace: true});
            return;
          }
        } catch (e) {
          console.error("Error checking if user password exists: ", e);
        }
      }

      try {
        await apiV2.auth.verifyInviteToken({token});
      } catch (e: any) {
        if (DEAD_INVITE_STATUSES.includes(e?.response?.status)) {
          setIsLinkDead(true);
        } else {
          console.error("Error checking the invite link: ", e);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkInvite();
  }, [email, isGoogleCallback, navigate, token]);

  const onSubmit = async (password: string): Promise<void> => {
    await apiV2.auth.setInvitePassword({token, password});

    if (email) {
      await loginUser({email, password});
      return;
    }

    navigate(LOGIN_PATH);
  };

  if (twoFactor) {
    return (
      <TwoFactorChallenge
        pendingToken={twoFactor.pendingToken}
        method={twoFactor.method}
        methods={twoFactor.methods}
        onComplete={completeSignIn}
        onCancel={resetTwoFactor}
      />
    );
  }

  return (
    <SignUpPresentation
      onSubmit={onSubmit}
      email={email}
      isLoading={isLoading}
      showLanguagePicker
      onGoogleSignUp={startGoogleSignUp}
      isGoogleBusy={isBusy}
      googleError={googleError}
      isInviteInvalid={isLinkDead || isInviteInvalid}
    />
  );
};

export default SignUp;
